import { Injectable } from '@angular/core';
import { PlatformAppService } from '@data/_generated-api/platform-api/services/platform-app.service';
import { ErrorInformation } from '@shared/models/error/error-information';

import Morphism from 'morphism';
import { err, ok } from '@common/result/Result';
import { AsyncResult } from '@common/result/asyncResult';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { EgiAppService } from '@/app/platform/platform-data/services/types';
import { NavigationItemDataEnricher } from '@/app/platform/platform-shared/enrichers/navigation-item-data-enricher';

@Injectable({
  providedIn: 'root'
})
export class EgiAppFacade implements EgiAppService {
  constructor(
    private readonly upamAppService: PlatformAppService,
    private readonly navigationItemEnricher: NavigationItemDataEnricher
  ) {}

  async create(model: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const body = model.creationModel;
    const result = await this.upamAppService.postApps({ body }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const egiApp = Morphism.map(EgiAppEntity, result.value);
    this.navigationItemEnricher.enrichWithEgiPages(egiApp);

    return ok(egiApp);
  }

  async update(model: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const result = await this.upamAppService.putApps({ body: model.editingModel }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const egiApp = Morphism.map(EgiAppEntity, result.value);
    this.navigationItemEnricher.enrichWithEgiPages(egiApp);
    return ok(egiApp);
  }

  async delete(model: EgiAppEntity): AsyncResult<EgiAppEntity> {
    const result = await this.upamAppService.deleteAppsId({ id: model.id }).toPromise();
    if (!result.isValid) {
      return err();
    }

    const egiApp = Morphism.map(EgiAppEntity, result.value);
    this.navigationItemEnricher.enrichWithEgiPages(egiApp);
    return ok(egiApp);
  }

  async getAll(): AsyncResult<EgiAppEntity[]> {
    const result = await this.upamAppService
      .postAppsQueryDatasource({
        body: {}
      })
      .toPromise();
    if (!result.isValid) {
      return err();
    }
    const egiApps = Morphism.map(EgiAppEntity, result.value.items);
    egiApps.forEach(a => this.navigationItemEnricher.enrichWithEgiPages(a));

    return ok(egiApps);
  }

  async getById(id: string): AsyncResult<EgiAppEntity> {
    const result = await this.upamAppService
      .postAppsQueryDatasource({
        body: {
          filterOptionGroup: {
            filterOptions: [
              {
                propertyName: 'id',
                value: id,
                operator: 'GUIDEQUALS'
              }
            ],
            filterOptionGroups: [],
            logicalOperator: ''
          }
        }
      })
      .toPromise();

    if (!result.isValid) {
      return err();
    }

    const egiApp = Morphism.map(EgiAppEntity, result.value.items[0]);

    this.navigationItemEnricher.enrichWithEgiPages(egiApp);
    return ok(egiApp);
  }
}
